import React from 'react'
import { Alert } from 'react-bootstrap'

import { Spinner } from '../../../../components/Spinner/Spinner'

import { type WealthboxEligibility } from './useWealthboxEligibility'

export const LARGE_BATCH_THRESHOLD = 50

export const WealthboxLoadingBody: React.FC = () => (
  <div className='d-flex flex-column align-items-center justify-content-center gap-3 py-5'>
    <Spinner />
    <div className='paragraph-medium medium'>Checking Wealthbox eligibility…</div>
  </div>
)

export const WealthboxNoEligibleAlert: React.FC = () => (
  <Alert variant='yellow' className='mb-0 d-flex align-items-start gap-2 paragraph-small'>
    <i className='bi bi-info-circle' />
    <span>None of the selected clients are synced with Wealthbox. Add their Wealthbox ID in client profiles to proceed.</span>
  </Alert>
)

interface WealthboxEligibilityNoticeProps {
  eligibility: WealthboxEligibility
  verbPhrase: string
}

export const WealthboxEligibilityNotice: React.FC<WealthboxEligibilityNoticeProps> = ({ eligibility, verbPhrase }) => {
  if (eligibility.status === 'error') {
    return (
      <Alert variant='danger' className='mb-0 d-flex align-items-start gap-2 paragraph-small'>
        <i className='bi bi-exclamation-triangle' />
        <span>We couldn't check Wealthbox eligibility for the selected clients. Please close this dialog and try again.</span>
      </Alert>
    )
  }

  if (eligibility.status !== 'ready' || eligibility.eligibleCount === 0) {
    return null
  }

  const largeBatch = eligibility.totalCount >= LARGE_BATCH_THRESHOLD
  const hasSkipped = eligibility.skippedCount > 0
  if (!largeBatch && !hasSkipped) {
    return null
  }

  return (
    <Alert variant='yellow' className='mb-0 d-flex align-items-start gap-2 paragraph-small'>
      <i className='bi bi-info-circle' />
      <span>
        {hasSkipped ? (
          <>
            You'll {verbPhrase}{' '}
            <strong>
              {eligibility.eligibleCount} of {eligibility.totalCount}
            </strong>{' '}
            clients in Wealthbox (<strong>{eligibility.skippedCount}</strong> not synced with Zocks).
          </>
        ) : (
          <>
            You are planning to {verbPhrase} <strong>{eligibility.totalCount}</strong> clients in Wealthbox.
          </>
        )}
        {largeBatch ? " This action can't be undone or stopped once started." : null}
      </span>
    </Alert>
  )
}
